import React, { memo } from 'react';
import { DocsLayout } from '../../components/docs/DocsLayout';
import { CodeBlock } from '../../components/docs/CodeBlock';
import { Callout } from '../../components/docs/Callout';
import { List, ArrowRight, AlertTriangle } from 'lucide-react';
export function PaginationGuidePage() {
  const paginationCode = {
    curl: `# First page
curl "https://api.obseq.com/v1/payments/payouts?limit=25" \\
  -H "Authorization: Bearer sk_live_..."

# Response:
# {
#   "data": [...],
#   "has_more": true,
#   "next_cursor": "cur_abc123"
# }

# Next page
curl "https://api.obseq.com/v1/payments/payouts?limit=25&starting_after=cur_abc123" \\
  -H "Authorization: Bearer sk_live_..."`,
    node: `// Fetch first page
let response = await fetch(
  'https://api.obseq.com/v1/payments/payouts?limit=25',
  {
    headers: { 'Authorization': \`Bearer \${apiKey}\` }
  }
);
let page = await response.json();

console.log('First page:', page.data);

// Fetch next page if available
if (page.has_more) {
  response = await fetch(
    \`https://api.obseq.com/v1/payments/payouts?limit=25&starting_after=\${page.next_cursor}\`,
    {
      headers: { 'Authorization': \`Bearer \${apiKey}\` }
    }
  );
  page = await response.json();
  console.log('Next page:', page.data);
}

// Fetch all pages
async function fetchAllPayouts() {
  const allPayouts = [];
  let cursor = null;
  
  do {
    const url = cursor
      ? \`https://api.obseq.com/v1/payments/payouts?limit=100&starting_after=\${cursor}\`
      : 'https://api.obseq.com/v1/payments/payouts?limit=100';
    
    const response = await fetch(url, {
      headers: { 'Authorization': \`Bearer \${apiKey}\` }
    });
    const page = await response.json();
    
    allPayouts.push(...page.data);
    cursor = page.has_more ? page.next_cursor : null;
  } while (cursor);
  
  return allPayouts;
}`,
    python: `import requests

# Fetch first page
response = requests.get(
    'https://api.obseq.com/v1/payments/payouts',
    params={'limit': 25},
    headers={'Authorization': f'Bearer {api_key}'}
)
page = response.json()

print('First page:', page['data'])

# Fetch next page if available
if page['has_more']:
    response = requests.get(
        'https://api.obseq.com/v1/payments/payouts',
        params={'limit': 25, 'starting_after': page['next_cursor']},
        headers={'Authorization': f'Bearer {api_key}'}
    )
    page = response.json()
    print('Next page:', page['data'])

# Fetch all pages
def fetch_all_payouts():
    all_payouts = []
    cursor = None
    
    while True:
        params = {'limit': 100}
        if cursor:
            params['starting_after'] = cursor
        
        response = requests.get(
            'https://api.obseq.com/v1/payments/payouts',
            params=params,
            headers={'Authorization': f'Bearer {api_key}'}
        )
        page = response.json()
        
        all_payouts.extend(page['data'])
        
        if not page['has_more']:
            break
        cursor = page['next_cursor']
    
    return all_payouts`,
    go: `// Fetch first page
resp, _ := http.Get("https://api.obseq.com/v1/payments/payouts?limit=25")
var page struct {
    Data       []Payout \`json:"data"\`
    HasMore    bool     \`json:"has_more"\`
    NextCursor string   \`json:"next_cursor"\`
}
json.NewDecoder(resp.Body).Decode(&page)

// Fetch next page if available
if page.HasMore {
    url := fmt.Sprintf(
        "https://api.obseq.com/v1/payments/payouts?limit=25&starting_after=%s",
        page.NextCursor,
    )
    resp, _ := http.Get(url)
    json.NewDecoder(resp.Body).Decode(&page)
}

// Fetch all pages
func fetchAllPayouts() ([]Payout, error) {
    var allPayouts []Payout
    cursor := ""
    
    for {
        url := "https://api.obseq.com/v1/payments/payouts?limit=100"
        if cursor != "" {
            url += "&starting_after=" + cursor
        }
        
        resp, _ := http.Get(url)
        var page struct {
            Data       []Payout \`json:"data"\`
            HasMore    bool     \`json:"has_more"\`
            NextCursor string   \`json:"next_cursor"\`
        }
        json.NewDecoder(resp.Body).Decode(&page)
        
        allPayouts = append(allPayouts, page.Data...)
        
        if !page.HasMore {
            break
        }
        cursor = page.NextCursor
    }
    
    return allPayouts, nil
}`,
    java: `// Fetch first page
HttpResponse<String> response = client.send(
    HttpRequest.newBuilder()
        .uri(URI.create("https://api.obseq.com/v1/payments/payouts?limit=25"))
        .header("Authorization", "Bearer " + apiKey)
        .GET()
        .build(),
    HttpResponse.BodyHandlers.ofString()
);

JSONObject page = new JSONObject(response.body());
JSONArray data = page.getJSONArray("data");

// Fetch next page if available
if (page.getBoolean("has_more")) {
    String cursor = page.getString("next_cursor");
    response = client.send(
        HttpRequest.newBuilder()
            .uri(URI.create("https://api.obseq.com/v1/payments/payouts?limit=25&starting_after=" + cursor))
            .GET()
            .build(),
        HttpResponse.BodyHandlers.ofString()
    );
}`,
    php: `<?php
// Fetch first page
$response = $client->get('https://api.obseq.com/v1/payments/payouts', [
    'query' => ['limit' => 25],
    'headers' => ['Authorization' => 'Bearer ' . $apiKey]
]);
$page = json_decode($response->getBody(), true);

echo 'First page: ' . count($page['data']) . " items\\n";

// Fetch next page if available
if ($page['has_more']) {
    $response = $client->get('https://api.obseq.com/v1/payments/payouts', [
        'query' => [
            'limit' => 25,
            'starting_after' => $page['next_cursor']
        ],
        'headers' => ['Authorization' => 'Bearer ' . $apiKey]
    ]);
    $page = json_decode($response->getBody(), true);
}

// Fetch all pages
function fetchAllPayouts($client, $apiKey) {
    $allPayouts = [];
    $cursor = null;
    
    do {
        $query = ['limit' => 100];
        if ($cursor) {
            $query['starting_after'] = $cursor;
        }
        
        $response = $client->get('https://api.obseq.com/v1/payments/payouts', [
            'query' => $query,
            'headers' => ['Authorization' => 'Bearer ' . $apiKey]
        ]);
        $page = json_decode($response->getBody(), true);
        
        $allPayouts = array_merge($allPayouts, $page['data']);
        $cursor = $page['has_more'] ? $page['next_cursor'] : null;
    } while ($cursor);
    
    return $allPayouts;
}`,
    ruby: `# Fetch first page
response = HTTParty.get(
  'https://api.obseq.com/v1/payments/payouts',
  query: { limit: 25 },
  headers: { 'Authorization' => "Bearer #{api_key}" }
)
page = response.parsed_response

puts "First page: #{page['data'].length} items"

# Fetch next page if available
if page['has_more']
  response = HTTParty.get(
    'https://api.obseq.com/v1/payments/payouts',
    query: { limit: 25, starting_after: page['next_cursor'] },
    headers: { 'Authorization' => "Bearer #{api_key}" }
  )
  page = response.parsed_response
end

# Fetch all pages
def fetch_all_payouts
  all_payouts = []
  cursor = nil
  
  loop do
    query = { limit: 100 }
    query[:starting_after] = cursor if cursor
    
    response = HTTParty.get(
      'https://api.obseq.com/v1/payments/payouts',
      query: query,
      headers: { 'Authorization' => "Bearer #{api_key}" }
    )
    page = response.parsed_response
    
    all_payouts.concat(page['data'])
    
    break unless page['has_more']
    cursor = page['next_cursor']
  end
  
  all_payouts
end`,
    dotnet: `// Fetch first page
var response = await client.GetAsync(
    "https://api.obseq.com/v1/payments/payouts?limit=25"
);
var json = await response.Content.ReadAsStringAsync();
var page = JsonSerializer.Deserialize<PageResponse>(json);

Console.WriteLine($"First page: {page.Data.Count} items");

// Fetch next page if available
if (page.HasMore)
{
    response = await client.GetAsync(
        $"https://api.obseq.com/v1/payments/payouts?limit=25&starting_after={page.NextCursor}"
    );
    json = await response.Content.ReadAsStringAsync();
    page = JsonSerializer.Deserialize<PageResponse>(json);
}

// Fetch all pages
async Task<List<Payout>> FetchAllPayouts()
{
    var allPayouts = new List<Payout>();
    string cursor = null;
    
    do
    {
        var url = cursor != null
            ? $"https://api.obseq.com/v1/payments/payouts?limit=100&starting_after={cursor}"
            : "https://api.obseq.com/v1/payments/payouts?limit=100";
        
        var response = await client.GetAsync(url);
        var json = await response.Content.ReadAsStringAsync();
        var page = JsonSerializer.Deserialize<PageResponse>(json);
        
        allPayouts.AddRange(page.Data);
        cursor = page.HasMore ? page.NextCursor : null;
    } while (cursor != null);
    
    return allPayouts;
}`
  };
  return <DocsLayout breadcrumbs={[{
    label: 'Docs',
    href: '/docs'
  }, {
    label: 'Guides',
    href: '/docs/guides'
  }, {
    label: 'Pagination',
    href: '/docs/guides/pagination'
  }]} onThisPage={[{
    title: 'Overview',
    id: 'overview'
  }, {
    title: 'How It Works',
    id: 'how-it-works'
  }, {
    title: 'Response Format',
    id: 'response-format'
  }, {
    title: 'Parameters',
    id: 'parameters'
  }, {
    title: 'Code Examples',
    id: 'examples'
  }, {
    title: 'Common Pitfalls',
    id: 'pitfalls'
  }]} data-id="element-1768">
      <h1 data-id="element-1769">Pagination</h1>
      <p className="lead" data-id="element-1770">
        Obseq uses cursor-based pagination for list endpoints, allowing you to
        efficiently iterate through large result sets.
      </p>

      <h2 id="overview" data-id="element-1771">Overview</h2>
      <p data-id="element-1772">
        All list endpoints (e.g., <code data-id="element-1773">GET /v1/payments/payouts</code>) return
        paginated results. Cursor-based pagination is more reliable than
        offset-based pagination when data changes frequently.
      </p>

      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 my-6" data-id="element-1774">
        <div className="flex items-start gap-3" data-id="element-1775">
          <List className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" data-id="element-1776" />
          <div data-id="element-1777">
            <h3 className="text-lg font-semibold text-blue-900 mb-2" data-id="element-1778">
              Why Cursor Pagination?
            </h3>
            <p className="text-sm text-blue-800" data-id="element-1779">
              Cursors provide consistent results even when items are added or
              removed between requests. Offset-based pagination can skip or
              duplicate items when data changes.
            </p>
          </div>
        </div>
      </div>

      <h2 id="how-it-works" data-id="element-1780">How It Works</h2>
      <p data-id="element-1781">Pagination follows this pattern:</p>

      {/* Flow Diagram */}
      <div className="my-8 bg-slate-50 border border-slate-200 rounded-lg p-8" data-id="element-1782">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-1783">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg px-4 py-3 text-center min-w-[140px]" data-id="element-1784">
            <span className="text-sm font-semibold text-blue-900" data-id="element-1785">
              Request Page 1
            </span>
            <code className="block text-xs text-blue-700 mt-1" data-id="element-1786">?limit=25</code>
          </div>
          <ArrowRight className="w-5 h-5 text-slate-400 rotate-90 md:rotate-0" data-id="element-1787" />
          <div className="bg-green-50 border-2 border-green-200 rounded-lg px-4 py-3 text-center min-w-[140px]" data-id="element-1788">
            <span className="text-sm font-semibold text-green-900" data-id="element-1789">
              Get Results
            </span>
            <code className="block text-xs text-green-700 mt-1" data-id="element-1790">
              + next_cursor
            </code>
          </div>
          <ArrowRight className="w-5 h-5 text-slate-400 rotate-90 md:rotate-0" data-id="element-1791" />
          <div className="bg-purple-50 border-2 border-purple-200 rounded-lg px-4 py-3 text-center min-w-[140px]" data-id="element-1792">
            <span className="text-sm font-semibold text-purple-900" data-id="element-1793">
              Request Page 2
            </span>
            <code className="block text-xs text-purple-700 mt-1" data-id="element-1794">
              ?starting_after=cur_...
            </code>
          </div>
        </div>
      </div>

      <h2 id="response-format" data-id="element-1795">Response Format</h2>
      <p data-id="element-1796">List endpoints return a consistent response structure:</p>
      <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto my-4" data-id="element-1797">
        <code data-id="element-1798">{`{
  "data": [
    {
      "id": "po_123",
      "amount": 250000,
      ...
    },
    ...
  ],
  "has_more": true,
  "next_cursor": "cur_abc123"
}`}</code>
      </pre>

      <h3 data-id="element-1799">Response Fields</h3>
      <table className="w-full text-sm my-4" data-id="element-1800">
        <thead data-id="element-1801">
          <tr className="border-b border-slate-200" data-id="element-1802">
            <th className="text-left py-2 pr-4" data-id="element-1803">Field</th>
            <th className="text-left py-2" data-id="element-1804">Description</th>
          </tr>
        </thead>
        <tbody data-id="element-1805">
          <tr className="border-b border-slate-100" data-id="element-1806">
            <td className="py-3 pr-4" data-id="element-1807">
              <code data-id="element-1808">data</code>
            </td>
            <td className="py-3 text-slate-600" data-id="element-1809">
              Array of resources for the current page
            </td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1810">
            <td className="py-3 pr-4" data-id="element-1811">
              <code data-id="element-1812">has_more</code>
            </td>
            <td className="py-3 text-slate-600" data-id="element-1813">
              Boolean indicating if more pages exist
            </td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1814">
            <td className="py-3 pr-4" data-id="element-1815">
              <code data-id="element-1816">next_cursor</code>
            </td>
            <td className="py-3 text-slate-600" data-id="element-1817">
              Cursor for the next page (null if no more pages)
            </td>
          </tr>
        </tbody>
      </table>

      <h2 id="parameters" data-id="element-1818">Parameters</h2>
      <p data-id="element-1819">Control pagination with these query parameters:</p>
      <table className="w-full text-sm my-4" data-id="element-1820">
        <thead data-id="element-1821">
          <tr className="border-b border-slate-200" data-id="element-1822">
            <th className="text-left py-2 pr-4" data-id="element-1823">Parameter</th>
            <th className="text-left py-2 pr-4" data-id="element-1824">Type</th>
            <th className="text-left py-2" data-id="element-1825">Description</th>
          </tr>
        </thead>
        <tbody data-id="element-1826">
          <tr className="border-b border-slate-100" data-id="element-1827">
            <td className="py-3 pr-4" data-id="element-1828">
              <code data-id="element-1829">limit</code>
            </td>
            <td className="py-3 pr-4" data-id="element-1830">integer</td>
            <td className="py-3 text-slate-600" data-id="element-1831">
              Number of results per page (default: 25, max: 100)
            </td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1832">
            <td className="py-3 pr-4" data-id="element-1833">
              <code data-id="element-1834">starting_after</code>
            </td>
            <td className="py-3 pr-4" data-id="element-1835">string</td>
            <td className="py-3 text-slate-600" data-id="element-1836">
              Cursor to fetch the next page
            </td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1837">
            <td className="py-3 pr-4" data-id="element-1838">
              <code data-id="element-1839">ending_before</code>
            </td>
            <td className="py-3 pr-4" data-id="element-1840">string</td>
            <td className="py-3 text-slate-600" data-id="element-1841">
              Cursor to fetch the previous page
            </td>
          </tr>
        </tbody>
      </table>

      <Callout type="tip" data-id="element-1842">
        Use <code data-id="element-1843">limit=100</code> when fetching all pages to minimize API
        calls. The default is 25.
      </Callout>

      <h2 id="examples" data-id="element-1844">Code Examples</h2>
      <p data-id="element-1845">Fetch paginated results in your preferred language:</p>
      <CodeBlock code={paginationCode} data-id="element-1846" />

      <h2 id="pitfalls" data-id="element-1847">Common Pitfalls</h2>
      <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-6 my-6" data-id="element-1848">
        <div className="flex items-start gap-3 mb-4" data-id="element-1849">
          <AlertTriangle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" data-id="element-1850" />
          <div data-id="element-1851">
            <h3 className="text-lg font-semibold text-orange-900 mb-2" data-id="element-1852">
              Avoid These Mistakes
            </h3>
          </div>
        </div>

        <div className="space-y-4" data-id="element-1853">
          <div className="bg-white rounded-lg p-4" data-id="element-1854">
            <h4 className="font-semibold text-slate-900 mb-2" data-id="element-1855">
              ✗ Don't use offset-based pagination
            </h4>
            <p className="text-sm text-slate-600 mb-2" data-id="element-1856">
              Obseq doesn't support <code data-id="element-1857">?offset=50</code>. Use cursors
              instead.
            </p>
            <code className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded" data-id="element-1858">
              ✗ /v1/payouts?offset=50
            </code>
            <br data-id="element-1859" />
            <code className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded mt-2 inline-block" data-id="element-1860">
              ✓ /v1/payouts?starting_after=cur_abc123
            </code>
          </div>

          <div className="bg-white rounded-lg p-4" data-id="element-1861">
            <h4 className="font-semibold text-slate-900 mb-2" data-id="element-1862">
              ✗ Don't assume data.length equals limit
            </h4>
            <p className="text-sm text-slate-600" data-id="element-1863">
              The last page may have fewer items than the limit. Always check{' '}
              <code data-id="element-1864">has_more</code> to determine if more pages exist.
            </p>
          </div>

          <div className="bg-white rounded-lg p-4" data-id="element-1865">
            <h4 className="font-semibold text-slate-900 mb-2" data-id="element-1866">
              ✗ Don't cache cursors long-term
            </h4>
            <p className="text-sm text-slate-600" data-id="element-1867">
              Cursors are opaque strings that may expire or become invalid.
              Fetch fresh cursors each time you paginate.
            </p>
          </div>

          <div className="bg-white rounded-lg p-4" data-id="element-1868">
            <h4 className="font-semibold text-slate-900 mb-2" data-id="element-1869">
              ✗ Don't fetch all pages unnecessarily
            </h4>
            <p className="text-sm text-slate-600" data-id="element-1870">
              Only fetch all pages if you truly need all data. For most use
              cases, paginate on-demand as users navigate.
            </p>
          </div>

          <div className="bg-white rounded-lg p-4" data-id="element-1871">
            <h4 className="font-semibold text-slate-900 mb-2" data-id="element-1872">
              ✗ Don't ignore rate limits
            </h4>
            <p className="text-sm text-slate-600" data-id="element-1873">
              When fetching multiple pages, add delays between requests or use
              exponential backoff to avoid rate limiting.
            </p>
          </div>
        </div>
      </div>

      <h3 data-id="element-1874">Best Practices</h3>
      <ul data-id="element-1875">
        <li data-id="element-1876">
          Use <code data-id="element-1877">limit=100</code> for bulk operations to minimize API calls
        </li>
        <li data-id="element-1878">
          Always check <code data-id="element-1879">has_more</code> before fetching the next page
        </li>
        <li data-id="element-1880">
          Store cursors temporarily (in memory or session) but don't persist
          them
        </li>
        <li data-id="element-1881">
          Handle empty <code data-id="element-1882">data</code> arrays gracefully
        </li>
        <li data-id="element-1883">Add rate limit handling when fetching multiple pages</li>
        <li data-id="element-1884">
          Consider using webhooks instead of polling for real-time updates
        </li>
      </ul>

      <Callout type="note" data-id="element-1885">
        For complete pagination specifications, see{' '}
        <a href="#/docs/api-conventions" className="text-blue-600 hover:text-blue-700" data-id="element-1886">
          API Conventions
        </a>
        .
      </Callout>

      <div className="mt-12 pt-8 border-t border-slate-200" data-id="element-1887">
        <h3 className="text-lg font-semibold text-slate-900 mb-4" data-id="element-1888">
          Related Guides
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1889">
          <a href="#/docs/guides/errors" className="p-4 bg-slate-50 border border-slate-200 rounded-lg hover:border-blue-300 transition-colors" data-id="element-1890">
            <h4 className="font-semibold text-slate-900 mb-1" data-id="element-1891">
              Error Handling
            </h4>
            <p className="text-sm text-slate-600" data-id="element-1892">Handle pagination errors</p>
          </a>
          <a href="#/docs/api-conventions" className="p-4 bg-slate-50 border border-slate-200 rounded-lg hover:border-blue-300 transition-colors" data-id="element-1893">
            <h4 className="font-semibold text-slate-900 mb-1" data-id="element-1894">
              API Conventions
            </h4>
            <p className="text-sm text-slate-600" data-id="element-1895">
              Complete API specifications
            </p>
          </a>
        </div>
      </div>
    </DocsLayout>;
}